import {
  emptyEmailAsksView,
  firstNameOf,
  type EmailAskInput,
  type EmailAskResult,
  type EmailAskTurnOffInput,
  type EmailAsksView,
} from '../shared/email-asks';
import type { EngineCall } from './desktop-commands';
import type { PaigeEngagementChannel } from './engagement-channel';
import { sendLearningReadingOnce } from './learning-reading';

/** The contract's EmailAsks (`GET /v1/me/email-asks`). */
type ServerAsks = {
  email: string;
  learningNotes: { jobId: string; workspaceJobId: string | null; email: string }[];
  shortcutSheet: { email: string; day: string } | null;
};

export interface EmailAskChannel {
  /** Paige's `/v1`. */
  api: string;
  tenant?: string;
  /** The job's own channel (`/v1/jobs/{id}`) on a job's route; absent on the new-job route. */
  jobApi?: string;
  engagement: PaigeEngagementChannel;
  call: EngineCall;
}

/**
 * CapturePoint's email asks (shared/email-asks.ts) answered by Paige's API (docs/engineering/lifecycle-emails.md, 2.4):
 * the learning note at the foot of Learning › Results and the F1 sheet's "Email me this sheet". Every email goes to the
 * address the person signs in with, which the row shows and does not let them change (EmailAsk.tsx's PAIGE seam). The
 * speed report is the desktop's speed test, hidden in Paige, and is refused. "Also send me a few tips" is the tips
 * sign-up itself (the engagement record's consent), offered while the person is not getting them.
 *
 * Learning's own job is the routed job (another job is another route), so a note is turned on and off on the job's
 * channel; while one is on, the workbench sends the Learning screen's skill count once a day (learning-reading.ts).
 */
export function serverEmailAsks(channel: EmailAskChannel): Record<string, unknown> {
  const listeners = new Set<(view: EmailAsksView) => void>();
  let sheetHidden = false;
  const read = async (): Promise<EmailAsksView> => {
    const [asks, record] = await Promise.all([
      request<ServerAsks>(channel, `${channel.api}/me/email-asks`, 'GET'),
      channel.engagement.read().catch(() => null),
    ]);
    const view = viewOf(asks, record?.tips.state === 'opted-in', sheetHidden);
    followNote(channel, asks);
    return view;
  };
  const changed = async () => {
    const view = await read().catch(() => null);
    if (view) for (const listener of listeners) listener(view);
  };
  return {
    getEmailAsks: () => read().catch(() => emptyEmailAsksView),
    onEmailAsksChanged: (listener: (view: EmailAsksView) => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    sendEmailAsk: async (input: EmailAskInput): Promise<EmailAskResult> => {
      const result = await send(channel, input);
      if (result.outcome === 'done' && input.kind === 'shortcut-sheet') sheetHidden = false;
      if (result.outcome === 'done') void changed();
      return result;
    },
    turnOffEmailAsk: async (input: EmailAskTurnOffInput): Promise<void> => {
      // The sheet is one email that already went: Turn off only stops showing it here.
      if (input.kind === 'shortcut-sheet') sheetHidden = true;
      else if (input.kind === 'learning-note' && channel.jobApi)
        await request(channel, `${channel.jobApi}/learning-note`, 'DELETE');
      void changed();
    },
  };
}

function viewOf(asks: ServerAsks, tipsOn: boolean, sheetHidden: boolean): EmailAsksView {
  const person = typeof window === 'undefined' ? undefined : window.paigeWorkbench?.person;
  return {
    available: true,
    prefill: { email: asks.email, firstName: firstNameOf(person?.name) },
    tipsOn,
    learningNotes: Object.fromEntries(
      asks.learningNotes.flatMap((note) =>
        note.workspaceJobId ? [[note.workspaceJobId, { email: note.email, waiting: false }]] : [],
      ),
    ),
    speedReport: null,
    shortcutSheet:
      asks.shortcutSheet && !sheetHidden
        ? { email: asks.shortcutSheet.email, waiting: false }
        : null,
  };
}

/** While a note is on for the routed job, today's skill count goes up once (learning-reading.ts). */
function followNote(channel: EmailAskChannel, asks: ServerAsks): void {
  const jobApi = channel.jobApi;
  if (!jobApi) return;
  const paigeJob = jobApi.slice(jobApi.lastIndexOf('/') + 1);
  const note = asks.learningNotes.find((entry) => entry.jobId === paigeJob && entry.workspaceJobId);
  if (note?.workspaceJobId)
    void sendLearningReadingOnce(channel.call, {
      workspaceJobId: note.workspaceJobId,
      jobApi,
      ...(channel.tenant ? { tenant: channel.tenant } : {}),
    });
}

/** The ask's request, or the refusal Paige gives without asking its API. */
function askOf(
  channel: EmailAskChannel,
  input: EmailAskInput,
  named: { firstName?: string },
): { url: string; method: string; body: unknown } | string {
  if (input.kind === 'speed-report') return 'Paige has no speed test to send.';
  if (input.kind === 'shortcut-sheet')
    return { url: `${channel.api}/me/email-asks/shortcut-sheet`, method: 'POST', body: named };
  if (!channel.jobApi) return 'Open the job first.';
  const icon = typeof input.jobIcon === 'number' ? { jobIcon: input.jobIcon } : {};
  return { url: `${channel.jobApi}/learning-note`, method: 'PUT', body: { ...named, ...icon } };
}

async function send(channel: EmailAskChannel, input: EmailAskInput): Promise<EmailAskResult> {
  const firstName = input.firstName?.trim();
  const ask = askOf(channel, input, firstName ? { firstName } : {});
  if (typeof ask === 'string') return { outcome: 'failed', message: ask };
  try {
    await request(channel, ask.url, ask.method, ask.body);
    if (input.alsoTips) await alsoTips(channel, input.email, firstName);
    return { outcome: 'done' };
  } catch (reason) {
    return {
      outcome: 'failed',
      message: reason instanceof Error ? reason.message : String(reason),
    };
  }
}

/** "Also send me a few tips": the tips sign-up, to the person's own address; a refusal there does not undo the ask. */
async function alsoTips(channel: EmailAskChannel, email: string, firstName: string | undefined) {
  const name =
    (typeof window === 'undefined' ? undefined : window.paigeWorkbench?.person?.name) ||
    firstName ||
    email;
  await channel.engagement.consent({ choice: 'opt-in', name, email }).catch(() => undefined);
}

async function request<T = unknown>(
  channel: EmailAskChannel,
  url: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      ...(channel.tenant ? { 'Paige-Tenant': channel.tenant } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.ok) return (response.status === 204 ? undefined : await response.json()) as T;
  let reason = 'That didn’t go through. Try again.';
  try {
    const problem = (await response.json()) as { detail?: string; title?: string };
    reason = problem.detail ?? problem.title ?? reason;
  } catch {
    /* The fallback says it. */
  }
  throw new Error(reason);
}
